import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { PARKING_LOT_COLUMNS, ParkingLotColumn, type ParkingLotComment, type ParkingLotItemDetail, type ParkingLotLoopOptions } from '@jabberr/core';
import { api, errorMessage } from '../lib/api.js';
import { initials, shortDate } from '../lib/format.js';
import { chipLabel, isNewProjectIdea, loopLine, namesJabberr, noteMeta, projectTint } from '../lib/parking.js';
import { Markdown } from './Markdown.js';
import { ErrorBox, Spinner } from './ui.js';

/**
 * An idea opened over the board (design I-Note): the sticky note grown into a card with its write-up, Build this,
 * Talk more with Jabberr, Move to, and the comment thread where @Jabberr answers. The board stays visible behind it
 * (no dark scrim); Escape, the close button or a click outside the note closes it.
 */
export function ParkingNote({
  itemId,
  team,
  colourBy,
  onClose,
  onChanged,
}: {
  itemId: string;
  /** The Tarawa view: Build this and Building are the team's. */
  team: boolean;
  colourBy: 'column' | 'project';
  onClose: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [item, setItem] = useState<ParkingLotItemDetail | null>(null);
  const [comments, setComments] = useState<ParkingLotComment[]>([]);
  const [loopOptions, setLoopOptions] = useState<ParkingLotLoopOptions | null>(null);
  const [sending, setSending] = useState(false);
  const [target, setTarget] = useState<string>('new');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ title: '', summary: '', writeup: '' });
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let alive = true;
    setItem(null);
    api.parkingLot
      .get(itemId)
      .then((r) => {
        if (!alive) return;
        setItem(r.item);
        setComments(r.comments);
        setLoopOptions(r.loopOptions ?? null);
        setTarget(r.loopOptions?.newProject ? 'new' : (r.loopOptions?.projects[0]?.id ?? 'new'));
      })
      .catch((err) => alive && setError(errorMessage(err)));
    return () => {
      alive = false;
    };
  }, [itemId]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /** True when the change was saved; on failure the error shows and the caller keeps what the person typed. */
  const save = async (patch: Parameters<typeof api.parkingLot.update>[1]): Promise<boolean> => {
    setError(null);
    try {
      const { item: next } = await api.parkingLot.update(itemId, patch);
      setItem(next);
      onChanged();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    }
  };

  const post = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    setNotice(null);
    setError(null);
    const asked = namesJabberr(body);
    setAsking(asked);
    try {
      const res = await api.parkingLot.comment(itemId, body);
      setComments((prev) => [...prev, res.comment, ...(res.reply ? [res.reply] : [])]);
      setDraft('');
      if (res.jabberr === 'unavailable') setNotice('Jabberr is not available right now. Your comment is posted; ask again later.');
      if (res.jabberr === 'no_conversation') setNotice('Jabberr answers from the conversation an idea came from. Press Talk more with Jabberr first, then ask here.');
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPosting(false);
      setAsking(false);
    }
  };

  // Use this: the suggestion becomes the person's own edit; Jabberr never changes the note by itself.
  const use = async (commentId: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.parkingLot.useSuggestion(itemId, commentId);
      setItem(r.item);
      setComments((prev) => [...prev.map((c) => (c.id === commentId ? { ...c, applied: true } : c)), r.edit]);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const build = async (): Promise<void> => {
    if (!item || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.parkingLot.build(itemId);
      navigate(`/p/${r.projectId}/c/${r.conversationId}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  // Part 2: the requirements go to Loop, where John or Adam approve them; the note shows what happened.
  const sendToLoop = async (): Promise<void> => {
    if (!item || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.parkingLot.sendToLoop(itemId, target === 'new' ? { mode: 'new' } : { mode: 'extend', projectId: target });
      setItem(r.item);
      setComments(r.comments);
      setLoopOptions(r.loopOptions ?? null);
      setSending(false);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const talk = async (): Promise<void> => {
    if (!item || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.parkingLot.talk(itemId);
      navigate(`/p/${r.projectId}/c/${r.conversationId}`, r.firstTurn ? { state: { firstTurn: r.firstTurn } } : undefined);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  // Continue the conversation (an idea project): its latest conversation, or a new one seeded with the note.
  const continueIdea = async (): Promise<void> => {
    if (!item || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.parkingLot.continueIdea(itemId);
      navigate(`/p/${r.projectId}/c/${r.conversationId}`, r.firstTurn ? { state: { firstTurn: r.firstTurn } } : undefined);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  const tint = item ? (colourBy === 'project' ? `tint-${projectTint(item.projectId)}` : `col-${item.column}`) : 'col-someday';
  const moveChoices = ParkingLotColumn.options.filter((c) => team || c !== 'building' || item?.column === 'building');

  return (
    <div className="note-layer" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <article role="dialog" aria-modal="true" aria-label={item?.title ?? 'Idea'} className={`note-card sticky ${tint}`}>
        <div className="note-top">
          <div className="note-heading">
            {item ? (
              <span className="note-eyebrow">
                {isNewProjectIdea(item) ? 'New project idea' : chipLabel(item)} · {PARKING_LOT_COLUMNS[item.column]}
                {team && item.audience === 'client' ? <span className="note-shared"> · Shared with the client</span> : null}
              </span>
            ) : null}
            {editing && item ? (
              <input className="note-title-input" aria-label="Title" value={edit.title} maxLength={120} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
            ) : (
              <h1 className="note-title">{item?.title ?? 'Loading'}</h1>
            )}
            {item ? <span className="note-meta">{noteMeta(item)}</span> : null}
          </div>
          <button ref={closeRef} type="button" className="note-close" aria-label="Close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {error ? <ErrorBox>{error}</ErrorBox> : null}
        {!item && !error ? <Spinner label="Opening the idea" /> : null}

        {item && editing ? (
          <div className="note-edit">
            <label>
              <span>One line</span>
              <input value={edit.summary} maxLength={200} onChange={(e) => setEdit({ ...edit, summary: e.target.value })} />
            </label>
            <label>
              <span>Write-up</span>
              <textarea rows={12} value={edit.writeup} maxLength={4000} onChange={(e) => setEdit({ ...edit, writeup: e.target.value })} />
            </label>
            <div className="note-edit-actions">
              <button type="button" className="btn btn-primary" onClick={() => void save({ title: edit.title.trim() || item.title, summary: edit.summary, writeup: edit.writeup }).then((saved) => { if (saved) setEditing(false); })}>
                Save
              </button>
              <button type="button" className="btn" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : item ? (
          <div className="note-body">
            {item.summary ? <p className="note-summary">{item.summary}</p> : null}
            {item.writeup ? <Markdown text={item.writeup} /> : <p className="muted">No write-up yet.</p>}
          </div>
        ) : null}

        {item ? (
          <>
            <div className="note-actions">
              {team ? (
                <button type="button" className="note-btn primary" onClick={() => void build()} disabled={busy} title="Drafts requirements from this idea. Send them to Loop for John's or Adam's OK; nothing is built before that.">
                  Build this
                </button>
              ) : null}
              {item.canContinue ? (
                <button type="button" className="note-btn" onClick={() => void continueIdea()} disabled={busy}>
                  Continue the conversation
                </button>
              ) : (
                <button type="button" className="note-btn" onClick={() => void talk()} disabled={busy}>
                  Talk more with Jabberr
                </button>
              )}
              {item.canEdit && !editing ? (
                <button type="button" className="note-btn" onClick={() => { setEdit({ title: item.title, summary: item.summary, writeup: item.writeup }); setEditing(true); }}>
                  Edit
                </button>
              ) : null}
              {item.canEdit ? (
                <label className="note-move">
                  <span>Move to</span>
                  <select value={item.column} onChange={(e) => void save({ column: e.target.value as ParkingLotColumn })}>
                    {moveChoices.map((c) => (
                      <option key={c} value={c}>
                        {PARKING_LOT_COLUMNS[c]}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
            {item.loop ? (
              <p className={`note-loop note-loop-${item.loop.status}`} role="status">
                {loopLine(item.loop)}
              </p>
            ) : null}
            {item.canSendToLoop && loopOptions && !sending ? (
              <div className="note-loop-send">
                <button type="button" className="note-btn primary" disabled={busy} onClick={() => setSending(true)}>
                  {item.loop ? 'Send to Loop again' : 'Send to Loop for approval'}
                </button>
                <span className="note-hint">John or Adam approve it in Loop; nothing is built before that.</span>
              </div>
            ) : null}
            {sending && loopOptions ? (
              <form className="note-loop-form" onSubmit={(e) => { e.preventDefault(); void sendToLoop(); }} aria-label="Send to Loop">
                <fieldset>
                  <legend>Send the requirements to Loop as</legend>
                  {loopOptions.newProject ? (
                    <label className="note-loop-choice">
                      <input type="radio" name="loop-target" value="new" checked={target === 'new'} onChange={() => setTarget('new')} />
                      <span>A new project, owned by you</span>
                    </label>
                  ) : null}
                  {loopOptions.projects.length > 0 ? (
                    <label className="note-loop-choice">
                      <input type="radio" name="loop-target" value="extend" checked={target !== 'new'} onChange={() => setTarget(loopOptions.projects[0]!.id)} />
                      <span>An addition to</span>
                      <select aria-label="Project to extend" value={target === 'new' ? '' : target} onChange={(e) => setTarget(e.target.value)} disabled={target === 'new'}>
                        {target === 'new' ? <option value="">a project…</option> : null}
                        {loopOptions.projects.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </fieldset>
                <div className="note-edit-actions">
                  <button type="submit" className="note-btn primary" disabled={busy || (target !== 'new' && !loopOptions.projects.some((p) => p.id === target)) || (target === 'new' && !loopOptions.newProject)}>
                    Send to Loop
                  </button>
                  <button type="button" className="note-btn" onClick={() => setSending(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}
            <div className="note-links">
              {item.sourceConversationId && item.sourceProjectId ? (
                <Link to={`/p/${item.sourceProjectId}/c/${item.sourceConversationId}`}>Open the conversation this came from</Link>
              ) : null}
              {item.buildTurnId && item.buildProjectId ? (
                <Link to={`/p/${item.buildProjectId}/outputs/${item.buildTurnId}`}>Open the requirements draft</Link>
              ) : null}
            </div>

            <section aria-label="Comments" className="note-comments">
              <h2>Talk about it · {comments.length}</h2>
              {team && item.audience === 'client' ? <p className="note-hint">The client can read this thread.</p> : null}
              {comments.map((c) => (
                <div key={c.id} className="note-comment">
                  {c.byJabberr ? (
                    <img className="note-avatar jabberr" src="/apple-touch-icon.png" alt="" />
                  ) : c.kind === 'loop' && !c.authorId ? (
                    <span className="note-avatar loop" aria-hidden="true">L</span>
                  ) : (
                    <span className="note-avatar" aria-hidden="true">
                      {initials(c.authorName ?? '?')}
                    </span>
                  )}
                  <div className="note-comment-body">
                    <span className="note-comment-who">
                      {c.byJabberr ? 'Jabberr' : (c.authorName ?? 'Someone')} <span className="note-comment-when">· {shortDate(c.createdAt)}</span>
                    </span>
                    {c.kind === 'edit' || c.kind === 'loop' ? (
                      <span className={`note-comment-text ${c.kind === 'loop' ? 'note-loop-record' : 'note-edit-record'}`}>{c.body}</span>
                    ) : c.byJabberr ? (
                      <Markdown text={c.body} />
                    ) : (
                      <span className="note-comment-text">{c.body}</span>
                    )}
                    {c.suggestion ? (
                      <div className={`note-suggestion${c.applied ? ' used' : ''}`}>
                        <span className="note-suggestion-part">Suggested {c.suggestion.part}</span>
                        <div className="note-suggestion-text">
                          <Markdown text={c.suggestion.text} />
                        </div>
                        {c.applied ? (
                          <span className="note-hint">Used</span>
                        ) : item.canEdit ? (
                          <button type="button" className="note-btn primary small" disabled={busy} onClick={() => void use(c.id)}>
                            Use this
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
              {asking ? <p className="note-hint" role="status">Jabberr is thinking…</p> : null}
              {notice ? <p className="note-hint" role="status">{notice}</p> : null}
              <form className="note-form" onSubmit={(e) => void post(e)}>
                <label htmlFor="note-comment" className="sr-only">
                  Add a comment
                </label>
                <input
                  id="note-comment"
                  type="text"
                  value={draft}
                  maxLength={4000}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={item.sourceConversationId ? 'Add a comment (@Jabberr to ask it)' : 'Add a comment'}
                />
                <button type="submit" className="note-btn primary" disabled={posting || !draft.trim()}>
                  Post
                </button>
              </form>
            </section>
          </>
        ) : null}
      </article>
    </div>
  );
}
